import { Component, HostListener, Inject, Input, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import {
  BullhornDepartment,
  BullhornPrivateLabelId,
  BullhornUserRestrictionsService,
  BullhornUserType,
} from 'bullhorn-user-restrictions-service-lib';
import { DocumentTypePickerComponent, FilterLabel, IDocumentType } from 'document-type-picker-lib';
import { WorkflowMessage } from 'interfaces-lib';
import { ILlm, LlmConfigService } from 'llm-config';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Subject, take } from 'rxjs';
import { FilterState } from 'user-restrictions-filter-lib';
import { environment } from '../../../../environments/environment';
import { MESSAGE_TYPE } from '../../../classes/ai-response-task';
import { sortNumbersAtEnd } from '../../../classes/collectionUtils';
import { StringUtils } from '../../../classes/stringUtils';
import { MAX_SELECTIONS } from '../../../constants/defaults';
import { AiPromptService } from '../../../services/ai-prompt.service';
import { BullhornDataService } from '../../../services/bullhorn-data.service';
import { IEntityLabelPair } from '../../../services/bullhorn-meta.service';
import { IModelMap, PromptProfileService } from '../../../services/prompt-profile.service';
import { IBullhornCardContext } from '../../../types/bullhorn-card-context';
import { BullhornEntityType } from '../../../types/bullhorn/bullhorn-entity-type';
import { IClientConfig } from '../../../types/client-config';
import { IMessageHandlerComponent } from '../../../types/component-method-message-handler';
import { IEntitySourceData } from '../../../types/IEntitySourceData';
import { getIPromptOptionsKeys, IPromptOptions } from '../../../types/IPromptProfileMeta';
import { IFilter, IPromptProfile } from '../../../types/prompt-profile';
import { DialogConfirmComponent } from '../../dialog-confirm/dialog-confirm.component';
import { IModalCloseEventData } from '../assist-manager-landing.component';

const DEFAULT_LLM_LIST = [
  'gpt-4o-mini',
  'gpt-4-turbo',
  'gpt-4o',
  'claude-3.5-sonnet',
  'claude-3-haiku',
  'gpt-4.1',
  'sonnet-4'
];

const ALLOWED_FILE_NAME_KEY = /^[a-zA-Z0-9_\- \[\]()${}.]$/;
const DISALLOWED_FILE_NAME_CHARS = /[^a-zA-Z0-9_\- \[\]()${}.]+/g;

@Component({
  selector: 'app-prompt-profile',
  templateUrl: './prompt-profile.component.html',
  styleUrls: ['./prompt-profile.component.scss'],
  standalone: false,
})
export class PromptProfileComponent implements OnInit, IMessageHandlerComponent {
  clientConfig!: IClientConfig;

  get labelFromForm() {
    const entityType = this.form.get('entityType')?.value;
    const labelPair = this.entities.find((pair) => pair.objectName === entityType);
    return labelPair ? labelPair.label : entityType;
  }

  get isNewConfig() {
    return this.promptProfile.action === undefined;
  }

  get allowSave(): boolean {
    // TODO: Allow to save if no LLMs
    return this.form.valid && this.form.errors === null;
  }
  protected readonly BullhornEntityType = BullhornEntityType;
  protected passdownEntityType?: BullhornEntityType;
  protected entities: IEntityLabelPair[] = [];
  protected sortedEntities: IEntityLabelPair[] = [];
  protected entityLabels = {
    Candidate: '',
    JobSubmission: '',
    JobOrder: '',
    ClientContact: '',
    Opportunity: '',
    Lead: '',
    Placement: '',
    ClientCorporation: '',
  };
  protected readonly StringUtils = StringUtils;
  protected form!: FormGroup;

  protected promptProfile: IPromptProfile;
  protected options: IPromptOptions;
  protected configTabInvalid = false;

  sourceFields: string[] = [];

  protected integrations = ['datarules', 'awesomedocs'];
  private readonly productFilterMap: Record<string, string> = {
    datarules: 'DataTools',
    awesomedocs: 'AwesomeDocs'
  };
  toggleValue = 'unpublished';
  protected isDatarules = false;
  protected dataToolsModels = ['gpt-4o-mini', 'gpt-4o'];

  protected selected: IPromptOptions;
  protected defaultHighlight = {
    role: '',
    tone: '',
    format: '',
    resultLength: '',
    explanationDifficulty: '',
    outputLanguage: '',
    LLMs: '',
  };
  protected tooltips = {
    role: '',
    tone: '',
    format: '',
    resultLength: '',
    explanationDifficulty: '',
    outputLanguage: '',
  };
  protected LLMList: ILlm[] = [];
  protected LLMsSelected: string[] | string = [];
  private cachedFullLLMList: ILlm[] = [];

  validationIconConfigurationTab: string | undefined;
  validationIconAdvancedTab: string | undefined;
  initialTabState = true;

  protected isFirstFieldCandidateId = true;
  protected candidateEntityIdFields: string[] = [];
  protected readonly MAX_SELECTIONS = MAX_SELECTIONS;
  protected readonly MAX_FILES_COUNT = 5;
  protected loadingResponse = false;
  protected aiResponse = '';
  protected aiGenerationProgress$ = new Subject<string>();
  protected userTypeFilterState: FilterState<BullhornUserType> | undefined;
  protected departmentFilterState: FilterState<BullhornDepartment> | undefined;
  protected privateLabelFilterState: FilterState<BullhornPrivateLabelId> | undefined;

  protected isFeatureFlagDTAIEnabled = false;
  protected isFeatureFlagADAIEnabled = false;
  protected promptFormatOptions: string[] = [];
  protected promptFormatTooltip = '';
  docxEnabled = false;
  @ViewChild('docxPanel') docxPanel: MatExpansionPanel | undefined;
  @ViewChild('documentTypePicker') documentTypePicker: DocumentTypePickerComponent | undefined;
  @Input()
  tinyApiKey = environment.tinyApiKey;
  private previousLLMSelections: string[] | string = [];
  protected currentLLMSelections: string[] | string = [];
  isDataRulesVisible = false; // Define the isDisabled property

  protected readonly FilterLabel = FilterLabel;
  isKyloePrompt = false;

  protected selectedFileType: IDocumentType | undefined;
  protected showOutputFile = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PromptProfileComponent>,
    private matDialog: MatDialog,
    private fb: FormBuilder,
    private bullhornDataService: BullhornDataService,
    private promptProfileService: PromptProfileService,
    private aiPromptService: AiPromptService,
    private messageNotificationService: MessageNotificationService,
    private llmConfigService: LlmConfigService,
    private bullhornUserRestrictionsService: BullhornUserRestrictionsService
  ) {
    this.promptProfile = data.promptProfile;
    this.passdownEntityType = data.promptProfile.entityType;
    this.selectedFileType = {
      id: this.promptProfile.fileConfig?.fileType ?? '',
      label: this.promptProfile.fileConfig?.fileType ?? '',
    };
    this.isKyloePrompt = !data.promptProfile.canCustomize;
    this.options = data.promptOptions;
    this.cachedFullLLMList = data.LLmList;
    this.LLMList = data.LLmList;
    this.selected = {
      role: [],
      tone: [],
      format: [],
      resultLength: [],
      explanationDifficulty: [],
      outputLanguage: [],
    };
    this.LLMsSelected = [];
    this.isFeatureFlagDTAIEnabled = data.DTAIEnabled;
    this.isFeatureFlagADAIEnabled = data.ADAIEnabled;
    this.isDataRulesVisible = data.isDataRulesVisible;
    if (
      (this.isFeatureFlagDTAIEnabled || this.isFeatureFlagADAIEnabled) &&
      this.promptProfile.integration
    ) {
      this.toggleValue = this.promptProfile.integration;
      // ToDo move 'datarules' to enum
      this.isDatarules = this.promptProfile.integration === 'datarules';
    } else if (this.promptProfile.enabled) {
      this.toggleValue = 'published';
    }

    this.entities = data.entities;
    this.getEntityLabels(this.entities);
    this.sortedEntities = [...this.entities].sort((a: IEntityLabelPair, b: IEntityLabelPair) =>
      a.objectName.localeCompare(b.objectName)
    );
  }

  ngOnInit(): void {
    this.docxEnabled = !!this.promptProfile.fileConfig?.enabled;
    this.form = this.fb.group({
      corpId: new FormControl(),
      promptId: new FormControl(),
      action: new FormControl(undefined, Validators.required),
      role: new FormControl(),
      tone: new FormControl(),
      resultLength: new FormControl(),
      format: new FormControl(),
      explanationDifficulty: new FormControl(),
      outputLanguage: new FormControl(),
      outputFieldName: new FormControl(),
      removeBias: new FormControl(),
      anonymise: new FormControl(),
      enableCopyButton: new FormControl(),
      enableSaveAsNote: new FormControl(),
      entityType: new FormControl(),
      additionalContext: new FormControl(),
      enabled: new FormControl(false),
      displayName: new FormControl(undefined, Validators.required),
      canCustomize: new FormControl(true),
      description: new FormControl(),
      sourceFields: new FormControl(),
      LLMs: new FormControl([], Validators.required),
      bullhornEntityId: new FormControl(),
      jobEntityId: new FormControl(),
      toggleStatus: new FormControl('candidateId'),
      aiResponse: new FormControl(),
      integration: new FormControl(),
      noteFilter: new FormControl(),
      noteFilterEnabled: new FormControl(),
      noteFilterCategory: new FormControl(),
      noteFilterDate: new FormControl(),
      noteFilterCount: new FormControl(),
      noteFilterAutoInclude: new FormControl(),
      noteFilterSort: new FormControl(),
      fileFilter: new FormControl(),
      fileFilterEnabled: new FormControl(),
      fileFilterCategory: new FormControl(),
      fileFilterDate: new FormControl(),
      fileFilterCount: new FormControl(),
      fileFilterAutoInclude: new FormControl(),
      fileFilterSort: new FormControl(),
      allowedUserTypes: new FormControl([]),
      allowedDepartments: new FormControl([]),
      allowedPrivateLabels: new FormControl([]),
      fileConfig: new FormGroup({
        fileName: new FormControl('', this.validateFileNameIfDocxEnabled.bind(this)),
        fileType: new FormControl(''),
        enabled: new FormControl(false),
      }),
    });

    getIPromptOptionsKeys().forEach((field) => {
      if (!Array.isArray(this.promptProfile[field])) {
        this.promptProfile[field] = [this.promptProfile[field]];
      }
    });

    this.form.patchValue(this.promptProfile);

    // todo refactor this
    const parseIds = (value: unknown): number[] => {
      if (Array.isArray(value)) { return value; }
      if (typeof value === 'string') {
        try { return JSON.parse(value); } catch { return []; }
      }
      return [];
    };

    const userTypeBridgeGroup = new UntypedFormGroup({
      filterValues: new UntypedFormControl(parseIds(this.form.get('allowedUserTypes')?.value)),
    });
    const departmentBridgeGroup = new UntypedFormGroup({
      filterValues: new UntypedFormControl(parseIds(this.form.get('allowedDepartments')?.value)),
    });
    const privateLabelBridgeGroup = new UntypedFormGroup({
      filterValues: new UntypedFormControl(parseIds(this.form.get('allowedPrivateLabels')?.value)),
    });

    this.bullhornUserRestrictionsService.getUserTypes().subscribe(types => {
      this.userTypeFilterState = { formGroup: userTypeBridgeGroup, srcList: types };
    });
    this.bullhornUserRestrictionsService.getDepartments().subscribe(depts => {
      this.departmentFilterState = { formGroup: departmentBridgeGroup, srcList: depts };
    });
    this.bullhornUserRestrictionsService.getPrivateLabels().subscribe(labels => {
      this.privateLabelFilterState = { formGroup: privateLabelBridgeGroup, srcList: labels };
    });
    if (this.promptProfile.enableCopyButton === undefined) {
      this.form.get('enableCopyButton')?.setValue(true, { emitEvent: false });
    }
    if (this.promptProfile.enableSaveAsNote === undefined) {
      this.form.get('enableSaveAsNote')?.setValue(true, { emitEvent: false });
    }
    if (this.isNewConfig) {
      this.setDefaultValues();
    } else {
      let llmsFromDB = this.promptProfileService.updateLLMs(this.promptProfile.LLMs);
      if (!Array.isArray(llmsFromDB)) { llmsFromDB = [llmsFromDB]; }

      this.LLMsSelected = llmsFromDB.map(llmNameOrId => {
        const resolvedModel = this.llmConfigService.resolveLlmReplacement(
          llmNameOrId,
          this.cachedFullLLMList
        );
        return resolvedModel?.name || llmNameOrId;
      });
      if (this.LLMsSelected.length !== 0) {
        this.markDefaultLLM(this.getLLMsSelected());
        this.sortLLMList(this.defaultHighlight.LLMs);
      } else {
        this.sortLLMList();
      }
      getIPromptOptionsKeys().forEach((key) => {
        this.selected[key] = this.promptProfile[key].filter((item: string) =>
          this.options[key].includes(item)
        );
        if (this.selected[key].length !== 0) {
          this.markAsDefault(this.selected[key][0], key);
          this.options[key] = this.moveToFirstAndSort(
            this.options[key],
            this.defaultHighlight[key]
          );
          this.selected[key] = this.moveToFirstAndSort(
            this.selected[key],
            this.defaultHighlight[key]
          );
        } else {
          this.options[key] = sortNumbersAtEnd(this.options[key].sort());
        }
      });
    }
    this.setPromptFormatOptions(this.toggleValue);

    this.updateConfigurationTabValidationIcon();
    this.updateAdvancedTabValidationIcon();
    this.form.valueChanges.subscribe((value) => {
      this.initialTabState = false;
      this.updateConfigurationTabValidationIcon();
      this.updateAdvancedTabValidationIcon();
    });
    this.handleLLMRestrictions(this.toggleValue);
    this.form.get('LLMs')?.valueChanges.subscribe((value) => {
      this.LLMsSelected = value;
    });
    this.form.get('entityType')?.valueChanges.subscribe((value) => {
      this.passdownEntityType = value;
      this.updateSourceFields(value);
    });
    this.form.get('integration')?.valueChanges.subscribe((value) => {
      this.isDatarules = value === 'datarules';
      this.toggleOutputFieldName();
      this.showOutputFile = value === undefined;
    });
    getIPromptOptionsKeys().forEach((key) => {
      this.updateTooltipAndForm(key);
      this.form.get(key)?.valueChanges.subscribe((value) => {
        this.selected[key] = value;
      });
    });

    this.toggleOutputFieldName();
    this.subscribeAndMarkPristine('bullhornEntityId');
    this.subscribeAndMarkPristine('jobEntityId');
    this.subscribeAndMarkPristine('toggleStatus', (value) => {
      this.isFirstFieldCandidateId = value === 'candidateId';
    });
    this.subscribeAndMarkPristine('aiResponse');
  }

  checkInput(event: KeyboardEvent): void {
    if (!this.isAllowedKey(event)) {
      event.preventDefault();
    }
  }

  // load the document type list and get the list of allowed file types
  // validate the fileType form control
  // add validators and update the view
  ngAfterViewInit(): void {
    this.documentTypePicker!.observeReady()
      .pipe(take(1))
      .subscribe(() => {
        const allowedFileTypes = this.documentTypePicker!.getValidDocumentTypes().map(
          (type) => type.label
        );

        const fileTypeControl = this.form.get('fileConfig.fileType');
        if (!fileTypeControl) {
          return;
        }

        fileTypeControl.setValidators(this.buildFileTypeValidator(allowedFileTypes));
        fileTypeControl.updateValueAndValidity();
      });
  }

  // allow blank type or one of the values in the allowed list
  // do we want to do case sensitive stuff here?
  private buildFileTypeValidator(allowedFileTypes: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const docxEnabled = this.form?.get('fileConfig.enabled')?.value;
      if (!docxEnabled) {
        return null;
      }
      const inputValue = (control.value || '').trim();
      if (!inputValue) {
        return null;
      }
      return allowedFileTypes.includes(inputValue) ? null : { invalidFileType: true };
    };
  }

  // using this to get every value entered by the user to compare to the list of allowed types
  onFileTypeInput(inputValue: string) {
    const fileValue = this.form.get('fileConfig.fileType')!;
    fileValue.setValue(inputValue);
    fileValue.updateValueAndValidity();
  }

  private isAllowedKey(event: KeyboardEvent): boolean {
    const allowed = ALLOWED_FILE_NAME_KEY;

    const controlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Enter',
      'Escape',
    ];

    return (
      controlKeys.includes(event.key) || event.ctrlKey || event.metaKey || allowed.test(event.key)
    );
  }

  validateFileNameIfDocxEnabled(control: AbstractControl): ValidationErrors | null {
    const docxEnabled = this.form?.get('fileConfig.enabled')?.value;
    if (!docxEnabled) { return null; }

    const value = control.value?.trim();
    return value ? null : { required: true };
  }

  preventPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    if (!clipboardData) { return; }

    const text = clipboardData.getData('text');
    const sanitized = text.replace(DISALLOWED_FILE_NAME_CHARS, '');

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;

    target.setRangeText(sanitized, start, end, 'end');
  }

  isSendButtonDisabled(): boolean {
    let disabled = false;
    const isJobSubmission =
      this.form.get('entityType')?.value === BullhornEntityType.JOB_SUBMISSION;
    if (
      !this.form.get('bullhornEntityId')?.value ||
      this.loadingResponse ||
      (this.isFirstFieldCandidateId && !this.form.get('jobEntityId')?.value && isJobSubmission) ||
      (!this.isFirstFieldCandidateId &&
        this.candidateEntityIdFields.length === 0 &&
        isJobSubmission)
    ) {
      disabled = true;
    }
    return disabled;
  }

  private createTestProfile(formValues: any): IPromptProfile {
    const tempPromptProfile: IPromptProfile = { ...formValues };

    Object.keys(this.selected).forEach((key) => {
      const fieldKey = key as keyof IPromptProfile;
      if (!(fieldKey in tempPromptProfile)) {
        (tempPromptProfile[fieldKey] as string[]) =
          this.selected[fieldKey as keyof typeof this.selected];
      }
    });
    this.excludeFieldsFromProfile(tempPromptProfile);
    return tempPromptProfile;
  }

  private excludeFieldsFromProfile(profile: IPromptProfile): void {
    const excludedFields = [
      'bullhornEntityId',
      'jobEntityId',
      'toggleStatus',
      'aiResponse',
      'integration',
      'noteFilterEnabled',
      'noteFilterAction',
      'noteFilterDate',
      'noteFilterCount',
      'noteFilterAutoInclude',
      'noteFilterSort',
      'sourceFields',
    ];

    excludedFields.forEach((field) => {
      if (field in profile) {
        delete profile[field as keyof IPromptProfile];
      }
    });
  }

  addFileType(selectedFileType: IDocumentType | undefined) {
    if (selectedFileType && selectedFileType.id) {
      this.selectedFileType = selectedFileType;
      (this.form.get('fileConfig') as FormGroup).patchValue({ fileType: selectedFileType.label });
    }
  }

  onToggleDocxOutput(event: any): void {
    this.docxEnabled = event.checked;
    this.form.get('fileConfig.enabled')?.setValue(this.docxEnabled);

    // angular method that recalculates current value essentially
    // re run the validators
    // update the valid status - using this for fileConfig.enabled - to revalidate filename whether its needed or not
    this.form.get('fileConfig.fileName')?.updateValueAndValidity();
    this.form.get('fileConfig.fileType')?.updateValueAndValidity();

    this.docxEnabled ? this.docxPanel?.open() : this.docxPanel?.close();
  }

  onPanelHeaderClick(event: MouseEvent): void {
    if (!this.docxEnabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onSelected(integrationType: string): void {
    this.toggleValue = integrationType;
  }

  sendAiRequest(): void {
    this.loadingResponse = true;
    const formValues = this.form.value;
    const testProfile: IPromptProfile = this.createTestProfile(formValues);
    const formattedSourceFields = this.bullhornDataService.updateSourceData(
      formValues.sourceFields
    );
    const availableDataV7: IEntitySourceData[] = [];
    const getEntityRequests = (entityType: BullhornEntityType, entityId: number) =>
      this.bullhornDataService.getEntityByIdV7(entityType, entityId, formattedSourceFields);

    if (formValues.entityType === BullhornEntityType.JOB_SUBMISSION) {
      if (this.isFirstFieldCandidateId) {
        availableDataV7.push(
          getEntityRequests(BullhornEntityType.CANDIDATE, formValues.bullhornEntityId as number),
          getEntityRequests(BullhornEntityType.JOB_ORDER, formValues.jobEntityId as number)
        );
      } else {
        this.candidateEntityIdFields.forEach((candidateId) => {
          availableDataV7.push(
            getEntityRequests(BullhornEntityType.CANDIDATE, parseInt(candidateId, 10)),
            getEntityRequests(BullhornEntityType.JOB_ORDER, formValues.bullhornEntityId as number)
          );
        });
      }
    } else {
      availableDataV7.push(
        getEntityRequests(formValues.entityType, formValues.bullhornEntityId as number),
      );
    }
    let aiModel = this.LLMList.find(llm => llm.name === this.defaultHighlight.LLMs);
    if (!aiModel) {
      aiModel = this.cachedFullLLMList.find(llm => llm.name === this.defaultHighlight.LLMs);
    }
    if (!aiModel) {
      this.messageNotificationService.error('Selected model not found');
      this.loadingResponse = false;
      return;
    }
    const selectedTemperature = 0;
    this.aiPromptService.sendAiRequest(
      availableDataV7,
      this.promptProfileService.createPromptRequest(testProfile),
      {
        aiProvider: aiModel.provider,
        aiModel: aiModel.modelId,
        temperature: selectedTemperature,
      },

      // TODO : this.selectedFiles, unhardcode this values
      [],
      // TODO: this.fileDataOnly, unhardcode this values
      false,
      // TODO: of autoInclude is active, should this be populated?
      [],
      this.getBullHornCardContext(formValues.corpId),
      this
    );
  }

  getSecondaryIdFields(): string[] {
    return this.candidateEntityIdFields;
  }

  removeSecondaryIdFields(chip: string): void {
    const index = this.candidateEntityIdFields.indexOf(chip);
    if (index >= 0) {
      this.candidateEntityIdFields.splice(index, 1);
    }
  }

  getSourceFields(): string[] {
    const sourceFieldsControl = this.form.get('sourceFields');
    if (!sourceFieldsControl) {
      throw new Error('sourceFields control is not found in the form.');
    }
    return sourceFieldsControl.value;
  }

  removeSourceField(removedField: string): void {
    const index = this.sourceFields.indexOf(removedField);
    if (index !== -1) {
      this.sourceFields.splice(index, 1);
    }
    console.log(this.sourceFields);
  }

  discardLocalChanges(e: any = null): void {
    if (e) { e.preventDefault(); } // prevent submission of parent form.

    if (this.form.dirty) {
      const confirmDialogRef: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(
        DialogConfirmComponent,
        {
          width: '45vw',
          height: 'auto',
          autoFocus: true,
          data: {
            title: `Changes made`,
            titleIcon: 'info_outline',
            message: this.allowSave
              ? 'Would you like to save your changes? '
              : 'Would you like to go back, amend and save? ',
            positiveText: 'Discard',
            negativeText: 'Go Back',
            alternativeText: this.allowSave ? 'Save' : null,
          } as IDialogConfirm,
        }
      );

      confirmDialogRef.afterClosed().subscribe((takeAction: any) => {
        if (!takeAction) { return; } else { this.dialogRef.close(false); }

        if (takeAction === 'Save') {
          this.canSaveCheck();
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  canSaveCheck(): void {
    const issues = this.getSaveValidationIssues();

    if (issues.length > 0) {
      this.showValidationDialog(issues);
    } else {
      this.savePromptProfile();
    }
  }

  private getSaveValidationIssues(): string[] {
    const issues: string[] = [];

    if (this.hasIntegrationChangedFromAwesomeDocs()) {
      issues.push(
        `This prompt will be unpublished from Kyloe AwesomeDocs. Any templates using this prompt will no longer work.`
      );
    }

    if (this.hasEntityTypeChanged() && this.isIntegrationAwesomeDocs()) {
      issues.push(
        'You have changed the entity type, any templates using this prompt will no longer work.'
      );
    }

    return issues;
  }

  private showValidationDialog(issues: string[]): void {
    const dialogRef: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(
      DialogConfirmComponent,
      {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title: 'Caution',
          message: issues,
          positiveText: 'Proceed',
          negativeText: 'Cancel',
        } as IDialogConfirm,
      }
    );

    dialogRef.afterClosed().subscribe((takeAction: boolean) => {
      if (takeAction) { this.savePromptProfile(); }
    });
  }

  savePromptProfile(): void {
    if (this.LLMsSelected) {
      const llmNames = Array.isArray(this.LLMsSelected)
        ? this.moveToFirstAndSort(this.LLMsSelected, this.defaultHighlight.LLMs)
        : [this.LLMsSelected];

      const modelIds = llmNames.map(name =>
        this.LLMList.find(m => m.name === name)?.modelId || name
      );

      this.form.get('LLMs')?.setValue(
        Array.isArray(this.LLMsSelected) ? modelIds : modelIds[0]
      );
    }
    getIPromptOptionsKeys().forEach((key) => {
      if (this.selected[key].length !== 0) {
        this.selected[key] = this.moveToFirstAndSort(
          this.selected[key],
          this.defaultHighlight[key]
        );
        this.form.get(key)?.setValue(this.selected[key]);
      }
    });
    this.form.get('allowedUserTypes')?.setValue(
      this.userTypeFilterState?.formGroup?.get('filterValues')?.value ?? []
    );
    this.form.get('allowedDepartments')?.setValue(
      this.departmentFilterState?.formGroup?.get('filterValues')?.value ?? []
    );
    this.form.get('allowedPrivateLabels')?.setValue(
      this.privateLabelFilterState?.formGroup?.get('filterValues')?.value ?? []
    );

    this.dialogRef.close({
      action: 'save',
      promptProfile: this.form.value,
    } as IModalCloseEventData);
  }

  public isIntegrationPrompt(): boolean {
    const val = this.form.get('integration')?.value;
    return this.integrations.includes(val);
  }

  public showSaveToFileComponent(): boolean {
    const entityType = this.form.get('entityType')?.value;
    return (
      this.showOutputFile && !this.isIntegrationPrompt() && entityType !== BullhornEntityType.LEAD
    );
  }

  protected onFilterChange(updatedFilter: IFilter, filterType: FilterLabel) {
    this.form.get(`${filterType.toLowerCase()}Filter`)?.setValue(updatedFilter);
    this.validateFilterCountSettings();
  }

  delete(): void {
    const displayName = this.form.get('displayName')?.value;
    const integration = this.form.get('integration')?.value;

    const issues: string[] = [`Are you sure you want to delete ${displayName}?`];
    if (integration === 'awesomedocs') {
      issues.push('Any Awesomedocs template using this prompt will no longer work.');
    }

    const dialogDelete: MatDialogRef<DialogConfirmComponent> = this.matDialog.open(
      DialogConfirmComponent,
      {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title: `Delete: ${displayName}`,
          message: issues,
          positiveText: 'Yes',
          negativeText: 'No',
        } as IDialogConfirm,
      }
    );

    dialogDelete.afterClosed().subscribe((takeAction: boolean) => {
      if (takeAction) {
        this.dialogRef.close({
          action: 'delete',
          promptProfile: this.form.value,
        } as IModalCloseEventData);
      }
    });
  }

  private moveToFirstAndSort(arr: string[], item: string): string[] {
    const arrAlphabetical = sortNumbersAtEnd(arr.sort());
    const index = arrAlphabetical.indexOf(item);
    if (index !== -1) {
      arrAlphabetical.splice(index, 1);
    }
    arrAlphabetical.unshift(item);

    return arrAlphabetical;
  }

  protected markAsDefault(item: string, key: keyof IPromptOptions, event?: Event) {
    if (event) {
      this.form.markAsDirty();
      event?.stopPropagation();
    }
    this.defaultHighlight[key] = item;
    const index = this.selected[key].indexOf(item);
    if (index !== -1) {
      this.selected[key].splice(index, 1);
    }
    this.selected[key].unshift(item);
    this.updateTooltipAndForm(key);
  }

  private subscribeAndMarkPristine = (controlName: string, callback?: (value: any) => void) => {
    const control = this.form.get(controlName);
    control?.valueChanges.subscribe((value) => {
      callback?.(value);
      control.markAsPristine();
    });
  }

  protected updateDefaultLLM(event: any) {
    if (event) {
      this.form.markAsDirty();
      event.event?.stopPropagation();
    }
    this.markDefaultLLM(event.LLM);
  }

  protected markDefaultLLM(item: string) {
    //  todo refactor to own method
    const mapping = PromptProfileService.modelList.find(
      (entry: IModelMap) => entry.oldModel === item
    );
    if (mapping) {
      item = mapping.newModel;
    }
    this.defaultHighlight['LLMs'] = item;
    const index = this.LLMsSelected.indexOf(item);
    if (Array.isArray(this.LLMsSelected)) {
      if (index !== -1) {
        this.LLMsSelected.splice(index, 1);
      }
      this.LLMsSelected.unshift(item);
    }
    this.buildLLMsList();
  }

  private toggleFileNameValidation(integrationType: string): void {
    const fileNameControl = this.form.get('fileConfig.fileName');
    if (!fileNameControl) { return; }

    if (this.isIntegrationPrompt()) {
      fileNameControl.clearValidators();
    } else {
      fileNameControl.setValidators(this.validateFileNameIfDocxEnabled.bind(this));
    }
    fileNameControl.updateValueAndValidity();
  }

  handleLLMRestrictions(selectedType: string): void {
    this.isDatarules = selectedType === 'datarules';
    if (!this.isDataRulesVisible) {
      this.previousLLMSelections = this.form.get('LLMs')?.value || [];
    }

    if (this.isDatarules) {
      this.previousLLMSelections = this.form.get('LLMs')?.value || [];
      this.filterLLMsByProduct('datarules');

      let llmValue = Array.isArray(this.promptProfile?.LLMs) ? this.promptProfile.LLMs[0] : this.promptProfile?.LLMs;

      const productFilter = this.productFilterMap['datarules'] || 'DataTools';
      const resolvedModel = this.llmConfigService.resolveLlmReplacement(
        llmValue,
        this.cachedFullLLMList,
        productFilter
      );
      llmValue = resolvedModel?.name || llmValue;

      const availableLLMs = this.LLMList.map(llm => llm.name);
      const defaultDatarulesModel = this.LLMList.find(llm => llm.modelId === 'kyloe-DT-4o-mini')?.name;
      this.LLMsSelected = availableLLMs.includes(llmValue)
        ? llmValue
        : (defaultDatarulesModel && availableLLMs.includes(defaultDatarulesModel) ? defaultDatarulesModel : availableLLMs[0]);
      this.defaultHighlight.LLMs = typeof this.LLMsSelected === 'string' ? this.LLMsSelected : this.LLMsSelected[0];
      this.form.get('LLMs')?.setValue(this.LLMsSelected);
    } else { // not datarules i.e. multiselect LLM component
      let previousSelection: string | string[] = this.setPreviousSelected();
      this.filterLLMsByProduct(selectedType);
      if (!Array.isArray(previousSelection)) {
        previousSelection = [previousSelection];
      }
      const availableLLMs = this.LLMList.map(llm => llm.name);
      const productFilter = this.productFilterMap[selectedType] || 'Assist';

      // Use the centralized replacement resolver for each model with product filter
      previousSelection = [...new Set(previousSelection.map(llmNameOrId => {
        const resolvedModel = this.llmConfigService.resolveLlmReplacement(
          llmNameOrId,
          this.cachedFullLLMList,
          productFilter
        );
        return resolvedModel?.name || llmNameOrId;
      }).filter(llm => availableLLMs.includes(llm)))];

      this.LLMsSelected = previousSelection.length > 0 ? previousSelection : availableLLMs;
      this.form.get('LLMs')?.setValue(this.LLMsSelected);
      this.buildLLMsList();
    }
    this.form
      .get('enabled')
      ?.setValue(selectedType === 'published' || this.integrations.includes(selectedType));
    if (
      (this.isFeatureFlagDTAIEnabled || this.isFeatureFlagADAIEnabled) &&
      this.integrations.includes(selectedType)
    ) {
      this.form.get('integration')?.setValue(selectedType);
    } else {
      this.form.get('integration')?.setValue(undefined);
    }
    this.toggleFileNameValidation(selectedType);
    this.setPromptFormatOptions(selectedType, true);
    this.validateFilterCountSettings();
  }

  private filterLLMsByProduct(productType: string): void {
    const productFilter = this.productFilterMap[productType] || 'Assist';
    this.LLMList = this.cachedFullLLMList
      .filter((llm: ILlm) => llm.products?.includes(productFilter) && !llm.replacement);
    this.sortLLMList();
  }

  private validateFilterCountSettings(): void {
    setTimeout(() => {
      const fileFilter = this.form.get('fileFilter');
      const fileFilterValue = fileFilter?.value || {};
      if (this.isDatarules && fileFilterValue?.isFilterEnabled) {
        if (fileFilterValue?.count > this.MAX_FILES_COUNT) {
          fileFilter?.setValue({ ...fileFilterValue, count: this.MAX_FILES_COUNT });
          this.messageNotificationService.info(
            `DataTools prompts are limited to ${this.MAX_FILES_COUNT} files`
          );
        }
      }
    }, 50);
  }

  protected LLMSelectionChange($event: string | string[]): void {
    this.LLMsSelected = $event;
    this.buildLLMsList();
  }

  protected buildLLMsList(): void {
    const oldModels = PromptProfileService.modelList.map((mapping) => mapping.oldModel);
    if (!this.isDatarules && Array.isArray(this.LLMsSelected)) {
      this.LLMsSelected = this.LLMsSelected.filter((llm) => !oldModels.includes(llm));
    }
    this.form.get('LLMs')?.setValue(this.LLMsSelected);
    // If default option is unselected, top option becomes new default
    if (
      Array.isArray(this.LLMsSelected) &&
      !this.LLMsSelected.includes(this.defaultHighlight.LLMs)
    ) {
      this.defaultHighlight.LLMs = this.getLLMsSelected();
    }
  }

  private getEntityLabels(entities: IEntityLabelPair[]): void {
    const entityLabelKeys = Object.keys(this.entityLabels) as (keyof typeof this.entityLabels)[];
    entityLabelKeys.forEach((key) => {
      entities.forEach((item) => {
        if (item.objectName === key) {
          this.entityLabels[key] = item.label;
        }
      });
    });
  }

  private hasIntegrationChangedFromAwesomeDocs(): boolean {
    return (
      this.promptProfile.integration === 'awesomedocs' &&
      this.form.get('integration')?.value !== 'awesomedocs'
    );
  }

  private hasEntityTypeChanged(): boolean {
    return this.promptProfile.entityType !== this.form.get('entityType')?.value;
  }

  private isIntegrationAwesomeDocs(): boolean {
    return this.form.get('integration')?.value === 'awesomedocs';
  }

  protected chipTooltipPlaceholder(entityType: string): string {
    return 'Enter ' + entityType + ' ID\'s to test against';
  }

  protected updateTooltipAndForm(key: keyof typeof this.tooltips): void {
    const formValue = this.form.get(key)?.value;
    if (formValue && Array.isArray(formValue)) {
      this.tooltips[key] = formValue.join(', ');
      this.form.get(key)?.setValue(this.selected[key]);
    }

    // If default option is unselected, top option becomes new default
    if (!this.selected[key].includes(this.defaultHighlight[key])) {
      this.defaultHighlight[key] = this.selected[key][0];
    }
  }

  private isConfigTabInvalid(): boolean {
    const controls = this.form.controls;
    for (const controlName in this.form.controls) {
      if (controlName !== 'LLMs' && controls[controlName].invalid) { return true; }
    }
    return false;
  }

  private updateConfigurationTabValidationIcon(): void {
    this.configTabInvalid = this.isConfigTabInvalid();
    setTimeout((_) => {
      if (this.configTabInvalid) { this.validationIconConfigurationTab = 'error'; } else { this.validationIconConfigurationTab = 'check_circle'; }
    }, 300); // Timing with animations
  }

  // Can be removed if we decide files do not need to be added to test prompt.
  private getBullHornCardContext(corpId: string): IBullhornCardContext {
    // TODO : this can be a completely empty file as this is only used by the programme if
    // TODO : sourceFiles length is greater than 0, which it will be until implemented below.
    const bullhornCardContext: IBullhornCardContext = {
      corporationId: corpId,
      userId: 0,
      cardEntityId: 0,
      cardEntityType: {} as BullhornEntityType,
      cardEntityName: undefined,
    };
    return bullhornCardContext;
  }

  private updateAdvancedTabValidationIcon(): void {
    setTimeout((_) => {
      if (this.form.controls['LLMs'].valid) { this.validationIconAdvancedTab = 'check_circle'; } else { this.validationIconAdvancedTab = 'error'; }
    }, 300); // Timing with animations
  }

  updateSourceFields(entityType: string) {
    this.sourceFields = this.bullhornDataService.getDefaultPromptFieldsForEntity(
      entityType as BullhornEntityType
    ) as string[];
    this.form.get('sourceFields')?.setValue(this.sourceFields);
  }

  private setDefaultValues() {
    let fc = this.form.get('sourceFields');
    if (fc) {
      const entityType = this.promptProfile.entityType ?? BullhornEntityType.CANDIDATE;
      this.sourceFields = this.bullhornDataService.getDefaultPromptFieldsForEntity(
        entityType
      ) as string[];
      fc.setValue(this.sourceFields, { emitEvent: false });
    }
    this.filterLLMsByProduct('Assist');
    const allAvailableModels = this.LLMList.map(llm => llm.name);
    fc = this.form.get('LLMs');
    if (fc) {
      fc.setValue(allAvailableModels, { emitEvent: false });
    }
    this.LLMsSelected = allAvailableModels;

    fc = this.form.get('enableCopyButton');
    if (fc) {
      fc.setValue(true, { emitEvent: false });
    }
    fc = this.form.get('enableSaveAsNote');
    if (fc) {
      fc.setValue(true, { emitEvent: false });
    }
  }

  private setPromptFormatOptions(promptType: string, updateForm?: boolean) {
    const isAwesomeDocsPrompt = promptType === 'awesomedocs';
    if (isAwesomeDocsPrompt) {
      this.promptFormatOptions = this.options.format.filter(
        (x) => x === 'Plain Text' || x === 'HTML (no code tags)'
      );
      this.promptFormatTooltip =
        'AwesomeDocs prompts are currently limited to Plain Text or HTML Tags format only.';
    } else {
      this.promptFormatOptions = this.options.format;
      this.promptFormatTooltip = '';
    }
    this.selected['format'] = this.promptProfile['format'].filter((item: string) =>
      this.promptFormatOptions.includes(item)
    );
    if (updateForm) { this.updateTooltipAndForm('format'); }
  }

  private getLLMsSelected(): string {
    if (Array.isArray(this.LLMsSelected)) {
      return this.LLMsSelected[0];
    } else {
      return this.LLMsSelected;
    }
  }

  private setPreviousSelected(): string | string[] {
    if (this.previousLLMSelections.length > 0) { return this.previousLLMSelections; }
    if (Array.isArray(this.LLMsSelected) && this.LLMsSelected.length > 0) { return this.LLMsSelected; }
    if (typeof this.LLMsSelected === 'string' && this.LLMsSelected) { return [this.LLMsSelected]; }
    return this.promptProfile?.LLMs || this.LLMList.map(llm => llm.name);
  }

  private sortLLMList(moveToFirst?: string): void {
    const names = this.LLMList.map(llm => llm.name);
    const sortedNames = moveToFirst
      ? this.moveToFirstAndSort(names, moveToFirst)
      : sortNumbersAtEnd(names.sort());

    const llmMap = new Map(this.LLMList.map(llm => [llm.name, llm]));
    this.LLMList = sortedNames.map(name => llmMap.get(name)!).filter(Boolean);
  }

  private toggleOutputFieldName(): void {
    this.isDatarules
      ? this.form.get('outputFieldName')?.disable()
      : this.form.get('outputFieldName')?.enable();
  }

  public handleMessage(message: WorkflowMessage): void {
    if (message.type === MESSAGE_TYPE.AI_SERVICE_RESPONSE) {
      this.aiResponse = StringUtils.formatForHtml(message.message.message);
      this.form.get('aiResponse')?.setValue(this.aiResponse);
      this.loadingResponse = false;
    } else {
      this.aiGenerationProgress$.next(message.message.status);
    }
  }

  public handleError(message: WorkflowMessage): void {
    this.loadingResponse = false;
    this.messageNotificationService.error(`Error returning AI response : `, message.message);
  }

  public handleQuestion(message: WorkflowMessage): void {}

  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (this.matDialog.openDialogs.length === 1) {
      if (event.code === 'Escape') {
        this.dialogRef.close();
      }
    }
  }
}

export interface IDialogConfirm {
  title: string;
  titleIcon: string;
  message: string | string[];
  negativeText: string;
  positiveText: string;
  alternativeText: string;
}
